import { useState } from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import useProjectStore from "../../store/projectStore";
import { bladesApi, geometryApi } from "../../api/blades";
import { exportDesignDocx } from "../../utils/reportExport";

const FORMAT_STYLES = {
  docx:       "bg-bh-50     text-bh-700     border-bh-200",
  xml:        "bg-orange-50 text-orange-700 border-orange-200",
  zip:        "bg-green-50  text-green-700  border-green-200",
  stp:        "bg-blue-50   text-blue-700   border-blue-200",
  geomTurbo: "bg-purple-50 text-purple-700 border-purple-200",
};

function ExportCard({ title, description, format, prereq, prereqMessage, onDownload, comingSoon }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const handle = async () => {
    setError(null);
    setLoading(true);
    try { await onDownload(); }
    catch (e) { setError(e?.response?.data?.error || "Download failed"); }
    finally   { setLoading(false); }
  };

  const dimmed = prereq === false;

  return (
    <div className={`bg-white rounded-lg border p-4 flex items-start gap-4 transition-opacity
      ${dimmed ? "border-gray-100 opacity-60" : "border-gray-200"}`}>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-semibold text-gray-800">{title}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded border font-mono font-normal
            ${FORMAT_STYLES[format] ?? "bg-gray-50 text-gray-500 border-gray-200"}`}>
            .{format}
          </span>
        </div>
        <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
        {dimmed && prereqMessage && (
          <p className="text-xs text-amber-600 mt-1.5">{prereqMessage}</p>
        )}
        {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
      </div>

      {comingSoon ? (
        <span className="shrink-0 text-xs text-gray-400 border border-gray-200 rounded px-2.5 py-1.5 whitespace-nowrap">
          Coming soon
        </span>
      ) : (
        <button
          onClick={handle}
          disabled={dimmed || loading}
          className="shrink-0 flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg
            border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 transition-colors
            disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ArrowDownTrayIcon className={`h-3.5 w-3.5 ${loading ? "animate-bounce" : ""}`} />
          {loading ? "Preparing…" : "Download"}
        </button>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{title}</p>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

export default function ExportPage() {
  const { activeDesign, activeProject } = useProjectStore();
  const [csvHeader, setCsvHeader] = useState(false);

  if (!activeDesign) return <div className="p-6 text-sm text-gray-500">No design loaded.</div>;

  const cls          = activeDesign.construction_lines || [];
  const hasClines    = cls.some(cl => (cl.r_coords?.length ?? 0) > 1);
  const hasBeta      = cls.some(cl => (cl.beta_spline?.control_points?.length ?? 0) > 0);
  const hasThickness = cls.some(cl => (cl.thickness_spline?.control_points?.length ?? 0) > 0);

  const id   = activeDesign.id;
  const name = activeDesign.name;

  const handlePdfExport = async () => {
    const [viewsRes, curvRes, perfRes, beta1Res] = await Promise.allSettled([
      geometryApi.bladeViews(id),
      geometryApi.curvatureMap(id),
      bladesApi.performanceCoefficients(id),
      bladesApi.beta1Init(id),
    ]);
    const views    = viewsRes.status  === "fulfilled" ? viewsRes.value.data  : {};
    const curvData = curvRes.status   === "fulfilled" ? curvRes.value.data   : null;
    const perf     = perfRes.status   === "fulfilled" ? perfRes.value.data   : {};
    const beta1    = beta1Res.status  === "fulfilled" ? beta1Res.value.data  : {};
    await exportDesignDocx(activeDesign, activeProject, views, curvData, perf, beta1, window.Plotly);
  };

  return (
    <div className="p-6 space-y-8 max-w-2xl">

      <Section title="Report">
        <ExportCard
          title="Design report"
          description="Multi-page Word document with design metadata, meridional contour, blade angle and thickness distributions, 2D views (wrapping, conformal map) and 3D blade surface. Opens in Word, LibreOffice, and Pages."
          format="docx"
          prereq={hasClines}
          prereqMessage="Define at least one construction line with coordinates."
          onDownload={handlePdfExport}
        />
      </Section>

      <Section title="Design File">
        <ExportCard
          title="Generation settings"
          description="Complete blade design definition in the FastBlade XML format. Can be re-imported into any FastBlade project to restore the full geometry."
          format="xml"
          prereq={true}
          onDownload={() => bladesApi.downloadXml(id, name)}
        />
      </Section>

      <Section title="Geometry">
        <label className="flex items-center gap-2 px-1 pb-1 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={csvHeader}
            onChange={e => setCsvHeader(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-bh-600 focus:ring-bh-500"
          />
          <span className="text-xs text-gray-500">Include column header row  <span className="text-gray-400">(x, y, z)</span></span>
        </label>
        <ExportCard
          title="Meridional contour"
          description="Hub and shroud contours as x,y,z point lists (x=r, y=0, z=z) — one CSV per surface inside a ZIP. Ready for direct import into SolidWorks, CATIA, or any CAD tool that reads point curves."
          format="zip"
          prereq={hasClines}
          prereqMessage="Define at least one construction line with coordinates."
          onDownload={() => bladesApi.downloadMeridionalCsv(id, name, csvHeader)}
        />
        <ExportCard
          title="Profile curves"
          description="Blade skeleton per stream surface as Cartesian x,y,z (x=r·cosθ, y=r·sinθ, z=z) — one CSV per stream surface inside a ZIP. Import each file as a separate guide curve in CAD."
          format="zip"
          prereq={hasBeta}
          prereqMessage="Define beta angle distributions first."
          onDownload={() => bladesApi.downloadProfilesCsv(id, name, csvHeader)}
        />
      </Section>

      <Section title="CAD">
        <ExportCard
          title="Blade surface — STEP AP203"
          description="Pressure-side and suction-side B-spline surfaces as a STEP AP203 file (.stp). Opens in SolidWorks, CATIA, NX, FreeCAD, and any other STEP-compliant CAD tool. Coordinates in millimetres."
          format="stp"
          prereq={hasThickness}
          prereqMessage="Define thickness distributions first."
          onDownload={() => bladesApi.downloadStep(id, name)}
        />
      </Section>

      <Section title="CFD / Meshing">
        <ExportCard
          title="Ansys TurboGrid · Cadence Fidelity"
          description="Hub, shroud and blade profiles (PS + SS per span) in the .geomTurbo format — the standard exchange format for both Ansys TurboGrid and Cadence (NUMECA) Fine/Turbo meshing. Coordinates in mm."
          format="geomTurbo"
          prereq={hasBeta}
          prereqMessage="Define beta angle distributions first."
          onDownload={() => bladesApi.downloadGeomTurbo(id, name)}
        />
      </Section>

    </div>
  );
}
